// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties, Fragment } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize } from "../utils";
import { apiCall, apiCallEx } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";
import { RolesPermissionsPanel } from "./RolesPermissionsPanel";
import { SalesforceView } from "./SalesforceView";
import { ExtractionProfileManager } from "./ExtractionProfileManager";
import { PlatformHealthTab } from "./PlatformHealthTab";
import { SelfServiceTemplateManager } from "./SelfServiceTemplateManager";
import { ParentRequirementsManager } from "./ParentRequirementsManager";
import { ContractTypeManager } from "./admin/ContractTypeManager";
import { SharePointSettings } from "./admin/SharePointSettings";
import { DocuSignSettings } from "./admin/DocuSignSettings";
import { ESignAdmin } from "./admin/ESignAdmin";
import { EmailGraphSettings } from "./admin/EmailGraphSettings";
import { BackupStatus } from "./admin/BackupStatus";
import { CompanyEntitiesManager } from "./admin/CompanyEntitiesManager";
import { BusinessUnitManager } from "./admin/BusinessUnitManager";
import { PlaybookManager } from "./admin/PlaybookManager";
import { PromptTemplateManager } from "./admin/PromptTemplateManager";
import { RoutingRulesManager } from "./admin/RoutingRulesManager";
import { DraftUploadRulesManager } from "./admin/DraftUploadRulesManager";
import { SlaTargetsManager } from "./admin/SlaTargetsManager";
import { BudgetsManager } from "./admin/BudgetsManager";
import { HarveySettings } from "./admin/HarveySettings";
import { AiRoutingAdmin } from "./admin/AiRoutingAdmin";
import { HelpContentManager } from "./HelpContentManager";

export const AdminView = ({ currentUser = null, requests = [], repo = [], onAdd = undefined, onRefresh = undefined }: { currentUser?: UserRow | null; requests?: RequestRow[]; repo?: ContractRow[]; onAdd?: any; onRefresh?: any }) => {
  const { edition: scopeEdition } = useClientScope();
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [newUser, setNewUser] = useState<Record<string, any>>({ name: "", email: "", role: "requester", department: "", password: "", lawyer_specialty: "", send_welcome: true, require_2fa_on_welcome: false });
  const [sendWelcomeUser, setSendWelcomeUser] = useState<UserRow | null>(null);
  const [sendWelcomeRequire2FA, setSendWelcomeRequire2FA] = useState(false);
  const [sendWelcomeSubmitting, setSendWelcomeSubmitting] = useState(false);
  const [policy, setPolicy] = useState<{ min_length: number; require_upper: boolean; require_digit: boolean; require_special: boolean } | null>(null);
  const [policyLoading, setPolicyLoading] = useState(true);
  const [policySaving, setPolicySaving] = useState(false);
  const [pwForm, setPwForm] = useState<Record<string, any>>({ current: "", newPw: "", confirm: "" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [editUser, setEditUser] = useState<any>(null);
  const [editForm, setEditForm] = useState<Record<string, any>>({});
  const [resetPwUser, setResetPwUser] = useState<any>(null);
  const [resetPwValue, setResetPwValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPurge, setShowPurge] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState<any>(null);
  const [setup2FAData, setSetup2FAData] = useState<any>(null);
  const [confirm2FACode, setConfirm2FACode] = useState("");
  const [confirm2FAError, setConfirm2FAError] = useState("");
  const [disable2FACode, setDisable2FACode] = useState("");
  const [my2FAEnabled, setMy2FAEnabled] = useState(false);
  const [ipEntries, setIpEntries] = useState<any[]>([]);
  const [ipEnabled, setIpEnabled] = useState(false);
  const [newIp, setNewIp] = useState<Record<string, any>>({ ip_address: "", description: "" });
  const [adminTab, setAdminTab] = useState("settings");
  const [adminGroup, setAdminGroup] = useState("users_security");
  const adminGroups = [
    { id: 'users_security', label: 'Users & Security', subTabs: [{ id: 'settings', label: 'Users' }, { id: 'rolesPermissions', label: 'Roles & Permissions' }, { id: 'security', label: 'Security' }, { id: 'ssoProviders', label: 'SSO Providers' }] },
    { id: 'workflow', label: 'Workflow Config', subTabs: [
      { id: 'contractTypes', label: 'Contract Types' },
      { id: 'businessUnits', label: 'Business Units' },
      { id: 'routingRules', label: 'Routing Rules' },
      { id: 'draftUploadRules', label: 'Draft Upload Rules' },
      ...(scopeEdition === 'in_house' ? [{ id: 'slaTargets', label: 'SLA Targets' }, { id: 'budgets', label: 'Budgets' }] : []),
      { id: 'parentRules', label: 'Parent Requirements' },
      { id: 'companyEntities', label: 'Company Entities' },
    ] },
    { id: 'ai_models', label: 'AI & Models', subTabs: [{ id: 'harvey', label: 'Harvey AI' }, { id: 'playbooks', label: 'Playbooks' }, { id: 'prompts', label: 'Prompt Templates' }, { id: 'extractionProfiles', label: 'Extraction Profiles' }, { id: 'extractiondash', label: 'AI Accuracy' }, { id: 'aiRouting', label: 'AI Routing' }] },
    { id: 'integrations', label: 'Integrations', subTabs: [{ id: 'sharepoint', label: 'SharePoint' }, { id: 'esign', label: 'E-Signature' }, { id: 'emailGraph', label: 'Email / Graph' }, { id: 'salesforce', label: 'Salesforce' }] },
    { id: 'self_service', label: 'Self-Service', subTabs: [{ id: 'selfServiceTemplates', label: 'Templates' }, { id: 'help_content', label: 'Help Content' }] },
    { id: 'validation_tab', label: 'Validation', subTabs: [{ id: 'validation', label: 'Validation Queue' }] },
    { id: 'system', label: 'System Health', subTabs: [{ id: 'platformHealth', label: 'Platform Health' }, { id: 'backups', label: 'Backups' }] },
  ];
  const handleGroupChange = (gid: string) => { setAdminGroup(gid); const g = adminGroups.find(x => x.id === gid); if (g?.subTabs?.length) setAdminTab(g.subTabs[0].id); };

  const loadAdminData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoadingUsers(true);
    const [userData, ipData] = await Promise.all([
      apiCall("/users"),
      apiCall("/ip-allow"),
    ]);
    if (userData?.users) setUsers(userData.users);
    if (ipData) { setIpEntries(ipData.entries || []); setIpEnabled(ipData.enabled || false); }
    setLoadingUsers(false);
    const meData = await apiCall("/auth/me");
    if (meData?.user) setMy2FAEnabled(meData.user!.totpEnabled || false);
  }, []);

  useEffect(() => { loadAdminData(true); }, [loadAdminData]);

  // Load password policy for the Security tab.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPolicyLoading(true);
      try {
        const r = await apiCall("/admin/password-policy");
        if (!cancelled && r?.policy) setPolicy(r.policy);
      } finally {
        if (!cancelled) setPolicyLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-refresh admin data every 30s
  useEffect(() => {
    const interval = setInterval(() => loadAdminData(false), 30000);
    return () => clearInterval(interval);
  }, [loadAdminData]);

  const refreshUsers = async () => { const data = await apiCall("/users"); if (data?.users) setUsers(data.users); };

  const handleSetup2FA = async () => { const data = await apiCall("/auth/2fa/setup", { method: "POST" }); if (data?.qrCode) { setSetup2FAData(data); setConfirm2FACode(""); setConfirm2FAError(""); } };
  const handleConfirm2FA = async () => { if (confirm2FACode.length < 6) return; const data = await apiCall("/auth/2fa/confirm", { method: "POST", body: JSON.stringify({ code: confirm2FACode }) }); if (data?.enabled) { setSetup2FAData(null); setConfirm2FACode(""); setMy2FAEnabled(true); refreshUsers(); alert("Two-factor authentication enabled."); } else { setConfirm2FAError(data?.error || "Verification failed"); } };
  const handleDisable2FA = async () => { if (!disable2FACode) return; const data = await apiCall("/auth/2fa/disable", { method: "POST", body: JSON.stringify({ code: disable2FACode }) }); if (data && !data.enabled && data.message) { setDisable2FACode(""); setMy2FAEnabled(false); refreshUsers(); alert("Two-factor authentication disabled."); } else { alert(data?.error || "Failed to disable 2FA"); } };

  const addIp = async () => {
    if (!newIp.ip_address) return;
    const result = await apiCall("/ip-allow", { method: "POST", body: JSON.stringify(newIp) });
    if (result?.entry) { setIpEntries(prev => [result.entry, ...prev]); setNewIp({ ip_address: "", description: "" }); }
  };

  const deleteIp = async (id: string) => {
    await apiCall(`/ip-allow/${id}`, { method: "DELETE" });
    setIpEntries(prev => prev.filter((e: any) => e.id !== id));
  };

  const handleCreateUser = async () => {
    setErr(""); setMsg("");
    if (!newUser.name || !newUser.email) { setErr("Name and email required"); return; }
    if (!newUser.send_welcome && !newUser.password) { setErr("Initial password required when not sending a welcome email"); return; }

    // When send_welcome is on, the user sets their own password via the invite link.
    // Seed the row with a random throwaway password — the send-welcome call flips
    // must_change_password=true so it can never be used to log in.
    const payload: Record<string, any> = {
      name: newUser.name,
      email: newUser.email,
      role: newUser.role,
      department: newUser.department,
      lawyer_specialty: newUser.lawyer_specialty,
      password: newUser.send_welcome
        ? (Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2))
        : newUser.password,
    };
    const result = await apiCall("/users", { method: "POST", body: JSON.stringify(payload) });
    if (result?.user) {
      const createdId = result.user.id;
      if (newUser.send_welcome) {
        const welcomeRes = await apiCall(`/users/${createdId}/send-welcome`, {
          method: "POST",
          body: JSON.stringify({ require_2fa: newUser.require_2fa_on_welcome }),
        });
        if (welcomeRes?.ok && welcomeRes?.email_sent) {
          setMsg(`Created ${result.user.name} and sent welcome email.`);
        } else if (welcomeRes?.ok) {
          setMsg(`Created ${result.user.name}; invite token ready but email delivery failed (${welcomeRes.email_error || "unknown"}). Use Send Welcome to retry.`);
        } else {
          setMsg(`Created ${result.user.name}; welcome email could not be sent. Use Send Welcome to retry.`);
        }
      } else {
        setMsg(`User ${result.user.name} created successfully`);
      }
      setNewUser({ name: "", email: "", role: "requester", department: "", password: "", lawyer_specialty: "", send_welcome: true, require_2fa_on_welcome: false });
      setShowAddUser(false);
      refreshUsers();
    } else { setErr("Failed to create user. Email may already exist."); }
  };

  const handleChangePw = async () => {
    setErr(""); setMsg("");
    if (!pwForm.current || !pwForm.newPw) { setErr("Current and new password required"); return; }
    if (pwForm.newPw !== pwForm.confirm) { setErr("New passwords do not match"); return; }
    const token = localStorage.getItem("kontractai_token");
    try {
      const res = await fetch("/api/auth/password", {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: pwForm.current, newPassword: pwForm.newPw }),
      });
      const data = await res.json();
      if (res.ok) { setMsg("Password updated successfully"); setPwForm({ current: "", newPw: "", confirm: "" }); setShowChangePw(false); }
      else setErr(data.error || "Password change failed");
    } catch { setErr("Connection error"); }
  };

  const roleLabels: Record<string, string> = { admin: "System Admin", lawyer: "Lawyer", legalops: "Legal Operations", commercial_ops: "Commercial Operations", contract_manager: "Contract Manager", business_reviewer: "Business Reviewer", requester: "Business Requestor", executive: "Executive" };

  return (
    <div>
      {/* Primary tabs */}
      <div style={{ display: "flex", gap: 2, borderBottom: `2px solid ${T.brd}`, marginBottom: 0 }}>
        {adminGroups.map(g => (
          <button key={g.id} onClick={() => handleGroupChange(g.id)} style={{
            padding: "10px 18px", fontSize: 13, fontWeight: adminGroup === g.id ? 700 : 400,
            color: adminGroup === g.id ? T.acc : T.txM,
            backgroundColor: adminGroup === g.id ? T.accS : "transparent",
            border: "none", borderBottom: adminGroup === g.id ? `2px solid ${T.acc}` : "2px solid transparent",
            borderRadius: "4px 4px 0 0", cursor: "pointer", fontFamily: "inherit", marginBottom: -2,
          }}>{g.label}</button>
        ))}
      </div>
      {/* Sub-tabs */}
      {(() => { const ag = adminGroups.find(x => x.id === adminGroup); if (!ag || ag.subTabs.length <= 1) return null; return (
        <div style={{ display: "flex", gap: 4, marginTop: 12, marginBottom: 16 }}>
          {ag.subTabs.map(sub => (
            <button key={sub.id} onClick={() => setAdminTab(sub.id)} style={{
              padding: "6px 14px", fontSize: 12, fontWeight: adminTab === sub.id ? 600 : 400,
              color: adminTab === sub.id ? T.tx : T.txD,
              backgroundColor: adminTab === sub.id ? T.bg3 : "transparent",
              border: `1px solid ${adminTab === sub.id ? T.brd : "transparent"}`,
              borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
            }}>{sub.label}</button>
          ))}
        </div>
      ); })()}
      {adminTab === "validation" && <ValidationView />}
      {adminTab === "extractiondash" && <ExtractionDashView />}
      {adminTab === "salesforce" && <SalesforceView requests={requests} repo={repo} onAdd={onAdd} user={currentUser} />}
      {adminTab === "security" && (
        <div>
          <Card style={{ marginBottom: 16, maxWidth: 640, margin: "0 auto 16px" }} data-deploy-marker="wave4-password-policy-card">
            <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>
              Password Policy
            </div>
            <div style={{ fontSize: 11, color: T.txM, marginBottom: 12 }}>
              Applies to all password mutations: invite acceptance, self-service password change, and admin-initiated reset.
            </div>
            {policyLoading ? (
              <div style={{ fontSize: 12, color: T.txM }}>Loading policy...</div>
            ) : policy ? (
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 12, alignItems: "center", marginBottom: 10 }}>
                  <label style={{ fontSize: 12, color: T.tx }}>Minimum length</label>
                  <input
                    type="number"
                    min={4}
                    max={128}
                    value={policy.min_length}
                    onChange={e => setPolicy({ ...policy, min_length: parseInt(e.target.value, 10) || 8 })}
                    style={{ padding: "6px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: "inherit" }}
                  />
                </div>
                {[
                  { key: "require_upper",   label: "Require at least one uppercase letter" },
                  { key: "require_digit",   label: "Require at least one digit" },
                  { key: "require_special", label: "Require at least one special character" },
                ].map(opt => (
                  <label key={opt.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.tx, marginBottom: 8, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={!!(policy as any)[opt.key]}
                      onChange={e => setPolicy({ ...policy, [opt.key]: e.target.checked } as any)}
                    />
                    {opt.label}
                  </label>
                ))}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
                  <Btn
                    v="primary"
                    sz="sm"
                    disabled={policySaving}
                    onClick={async () => {
                      setPolicySaving(true);
                      try {
                        const r = await apiCall("/admin/password-policy", {
                          method: "PUT",
                          body: JSON.stringify(policy),
                        });
                        if (r?.ok) {
                          setMsg("Password policy updated.");
                          setPolicy(r.policy);
                        } else {
                          setErr(r?.error || "Failed to update policy");
                        }
                      } catch {
                        setErr("Connection error");
                      } finally {
                        setPolicySaving(false);
                      }
                    }}
                  >
                    {policySaving ? "Saving..." : "Save policy"}
                  </Btn>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: T.red }}>Failed to load policy.</div>
            )}
          </Card>
          <SecurityView />
        </div>
      )}
      {adminTab === "ssoProviders" && <SsoProvidersView />}
      {adminTab === "rolesPermissions" && <RolesPermissionsPanel />}
      {adminTab === "contractTypes" && <ContractTypeManager />}
      {adminTab === "companyEntities" && <CompanyEntitiesManager />}
      {adminTab === "sharepoint" && <>
        <Card style={{ marginBottom: 16, padding: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: T.tx, marginBottom: 6 }}>SharePoint Sync Management</div>
          <div style={{ fontSize: 11, color: T.txD, marginBottom: 12 }}>Re-sync all contracts missing SharePoint URLs. Safe to run multiple times.</div>
          <Btn v="primary" sz="sm" onClick={async () => { if (!window.confirm('Re-sync all contracts missing SharePoint URLs?')) return; const r = await apiCall('/extraction/sharepoint-resync', { method: 'POST', body: JSON.stringify({}) }); if (r) alert(`${r.message}\n\n${(r.results || []).map((x: any) => `${x.name?.substring(0, 50)}: ${x.status}`).join('\n')}`); }}>Re-sync Missing Contracts</Btn>
        </Card>
        <SharePointSettings />
      </>}
      {adminTab === "esign" && <ESignAdmin />}
      {adminTab === "emailGraph" && <EmailGraphSettings />}
      {adminTab === "extractionProfiles" && <ExtractionProfileManager />}
      {adminTab === "platformHealth" && <PlatformHealthTab />}
      {adminTab === "backups" && <BackupStatus />}
      {adminTab === "businessUnits" && <BusinessUnitManager />}
      {adminTab === "routingRules" && <RoutingRulesManager />}
      {adminTab === "draftUploadRules" && <DraftUploadRulesManager />}
      {adminTab === "playbooks" && <PlaybookManager />}
      {adminTab === "prompts" && <PromptTemplateManager />}
      {adminTab === "selfServiceTemplates" && <SelfServiceTemplateManager />}
      {adminTab === "help_content" && <HelpContentManager />}
      {adminTab === "parentRules" && <ParentRequirementsManager />}
      {adminTab === "harvey" && <HarveySettings />}
      {adminTab === "aiRouting" && <AiRoutingAdmin />}
      {adminTab === "slaTargets" && <SlaTargetsManager />}
      {adminTab === "budgets" && <BudgetsManager />}
      {adminTab === "settings" && <>
      <SectionTitle sub="Users, roles, permissions, configuration" right={
        <div style={{ display: "flex", gap: 8 }}>
          <Btn v="secondary" sz="sm" onClick={() => setShowChangePw(!showChangePw)}>Change My Password</Btn>
          <Btn v="primary" sz="sm" onClick={() => setShowAddUser(!showAddUser)}>+ Add User</Btn>
        </div>
      }>Administration</SectionTitle>

      {msg && <div style={{ padding: "8px 12px", backgroundColor: T.grnS, borderRadius: 5, marginBottom: 12, fontSize: 12, color: T.grn }}>{msg}</div>}
      {err && <div style={{ padding: "8px 12px", backgroundColor: T.redS, borderRadius: 5, marginBottom: 12, fontSize: 12, color: T.red }}>{err}</div>}

      {/* Change Password Form */}
      {showChangePw && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Change Password</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <Inp label="Current Password" value={pwForm.current} onChange={v => setPwForm((p: any) => ({ ...p, current: v }))} type="password" />
            <Inp label="New Password" value={pwForm.newPw} onChange={v => setPwForm((p: any) => ({ ...p, newPw: v }))} type="password" />
            <Inp label="Confirm New Password" value={pwForm.confirm} onChange={v => setPwForm((p: any) => ({ ...p, confirm: v }))} type="password" />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn v="secondary" sz="sm" onClick={() => setShowChangePw(false)}>Cancel</Btn>
            <Btn v="primary" sz="sm" onClick={() => { handleChangePw(); }}>Update Password</Btn>
          </div>
        </Card>
      )}

      {/* Two-Factor Authentication */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Two-Factor Authentication</div>
        {my2FAEnabled ? (
          <div>
            <div style={{ fontSize: 12, color: T.grn, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: T.grn, display: "inline-block" }} />2FA is enabled on your account</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="text" inputMode="numeric" maxLength={6} placeholder="Enter code to disable" value={disable2FACode} onChange={e => setDisable2FACode(e.target.value.replace(/[^0-9A-Fa-f]/g, ""))} style={{ padding: "6px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, width: 160, fontFamily: "monospace" }} />
              <Btn v="secondary" sz="sm" onClick={() => { handleDisable2FA(); }} disabled={!disable2FACode}>Disable 2FA</Btn>
            </div>
          </div>
        ) : setup2FAData ? (
          <div>
            <div style={{ textAlign: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: T.txM, marginBottom: 8 }}>Scan with Microsoft Authenticator, Google Authenticator, or any TOTP app:</div>
              <img src={setup2FAData.qrCode} alt="2FA QR Code" style={{ width: 200, height: 200, borderRadius: 8, backgroundColor: "#fff", padding: 8 }} />
              <div style={{ fontSize: 10, color: T.txD, marginTop: 8 }}>Manual entry: <code style={{ userSelect: "all", color: T.acc }}>{setup2FAData.secret}</code></div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.orn, marginBottom: 4 }}>Save your recovery codes now:</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, padding: 10, backgroundColor: T.bg3, borderRadius: 6 }}>{setup2FAData.recoveryCodes.map((c: string, i: number) => <div key={i} style={{ fontFamily: "monospace", fontSize: 12, color: T.tx }}>{c}</div>)}</div>
              <div style={{ fontSize: 10, color: T.red, marginTop: 4 }}>These codes will not be shown again. Save them securely.</div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="text" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={confirm2FACode} onChange={e => { setConfirm2FACode(e.target.value.replace(/[^0-9]/g, "")); setConfirm2FAError(""); }} onKeyDown={e => e.key === "Enter" && handleConfirm2FA()} style={{ padding: "6px 10px", borderRadius: 6, border: `2px solid ${confirm2FAError ? T.red : T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 14, width: 140, fontFamily: "monospace", textAlign: "center", letterSpacing: "0.2em" }} />
              <Btn v="primary" sz="sm" onClick={() => { handleConfirm2FA(); }} disabled={confirm2FACode.length < 6}>Verify & Enable</Btn>
              <Btn v="secondary" sz="sm" onClick={() => setSetup2FAData(null)}>Cancel</Btn>
            </div>
            {confirm2FAError && <div style={{ color: T.red, fontSize: 11, marginTop: 6 }}>{confirm2FAError}</div>}
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 12, color: T.txM, marginBottom: 10 }}>Add an extra layer of security by requiring a code from your authenticator app when signing in.</div>
            <Btn v="primary" sz="sm" onClick={() => { handleSetup2FA(); }}>Enable Two-Factor Authentication</Btn>
          </div>
        )}
      </Card>

      {/* Add User Form */}
      {showAddUser && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Create New User</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Inp label="Full Name" value={newUser.name} onChange={v => setNewUser((p: any) => ({ ...p, name: v }))} required />
            <Inp label="Email" value={newUser.email} onChange={v => setNewUser((p: any) => ({ ...p, email: v }))} type="email" required />
            <Sel label="Role" value={newUser.role} onChange={v => setNewUser((p: any) => ({ ...p, role: v }))} options={Object.entries(roleLabels).map(([value, label]) => ({ value, label }))} required />
            <Sel label="Department" value={newUser.department} onChange={v => setNewUser((p: any) => ({ ...p, department: v }))} options={DEPARTMENTS} />
            {!newUser.send_welcome && <Inp label="Initial Password" value={newUser.password} onChange={v => setNewUser((p: any) => ({ ...p, password: v }))} type="password" />}
            {newUser.role === "lawyer" && <Inp label="Specialty" value={newUser.lawyer_specialty} onChange={v => setNewUser((p: any) => ({ ...p, lawyer_specialty: v }))} placeholder="e.g., Commercial, IP, Data Privacy" />}
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.tx, gridColumn: "1 / -1", marginTop: 4 }}>
              <input type="checkbox" checked={!!newUser.send_welcome} onChange={e => setNewUser((p: any) => ({ ...p, send_welcome: e.target.checked }))} />
              Send welcome email after creation (recommended)
            </label>
            {newUser.send_welcome && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.tx, gridColumn: "1 / -1" }}>
                <input type="checkbox" checked={!!newUser.require_2fa_on_welcome} onChange={e => setNewUser((p: any) => ({ ...p, require_2fa_on_welcome: e.target.checked }))} />
                Require two-factor authentication for this user (totp_required = true)
              </label>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <Btn v="secondary" sz="sm" onClick={() => setShowAddUser(false)}>Cancel</Btn>
            <Btn v="primary" sz="sm" onClick={() => { handleCreateUser(); }} disabled={!newUser.name || !newUser.email}>Create User</Btn>
          </div>
        </Card>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Users from DB */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Users ({users.length})</div>
          {loadingUsers ? <div style={{ fontSize: 12, color: T.txM }}>Loading...</div> :
            users.map((u: UserRow) => (
              <div key={u.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
                <div><div style={{ fontSize: 12, color: T.tx }}>{u.name}</div><div style={{ fontSize: 10, color: T.txD }}>{u.email}</div></div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Badge c={T.acc} b={T.accS}>{roleLabels[u.role || ''] || u.role}</Badge>
                  {u.must_change_password && (
                    <span
                      style={{ fontSize: 10, color: T.red, fontWeight: 600, padding: "2px 8px", borderRadius: 4, backgroundColor: T.redS }}
                      title="User must change password on next login"
                    >
                      Pw Rotation Pending
                    </span>
                  )}
                  {u.totp_enabled && <span style={{ fontSize: 10, color: T.grn, fontWeight: 600, padding: "2px 8px", borderRadius: 4, backgroundColor: T.grnS }}>2FA</span>}
                  {u.totp_required && !u.totp_enabled && <span style={{ fontSize: 10, color: T.orn, fontWeight: 600, padding: "2px 8px", borderRadius: 4, backgroundColor: T.ornS }}>2FA Required</span>}
                  {!u.totp_required && !u.totp_enabled && <button onClick={async () => { const d = await apiCall(`/auth/2fa/require/${u.id}`, { method: "POST" }); if (d?.required) { refreshUsers(); setMsg(`2FA required for ${u.name}`); } }} style={{ fontSize: 10, color: T.acc, background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>Require 2FA</button>}
                  {u.totp_enabled && <button onClick={async () => { if (!window.confirm(`Reset 2FA for ${u.name}?`)) return; const d = await apiCall(`/auth/2fa/reset/${u.id}`, { method: "POST" }); if (d?.message) { refreshUsers(); setMsg(d.message); } }} style={{ fontSize: 10, color: T.orn, background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>Reset 2FA</button>}
                  <button
                    onClick={() => { setSendWelcomeUser(u); setSendWelcomeRequire2FA(false); }}
                    style={{ fontSize: 10, color: T.acc, background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}
                    title="Send a welcome email with a password-set link. Invalidates any prior unused invite tokens."
                  >
                    Send Welcome
                  </button>
                  <Btn v="ghost" sz="sm" onClick={() => { setEditUser(u); setEditForm({ name: u.name, email: u.email, role: u.role, specialty: u.specialty || "", department: u.department || "" }); }}>Edit</Btn>
                  <button onClick={() => { setResetPwUser(u); setResetPwValue(""); }} style={{ fontSize: 10, color: T.prp, background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>Reset Pw</button>
                  {u.is_active === false ? (
                    <>
                      <span style={{ fontSize: 10, color: T.txD, fontWeight: 600, padding: "2px 8px", borderRadius: 4, backgroundColor: T.bg4 }}>Deactivated</span>
                      <button onClick={async () => {
                        const res = await apiCallEx(`/users/${u.id}/reactivate`, { method: "POST" });
                        if (res.ok && res.data?.success) { refreshUsers(); setMsg(`${u.name} reactivated — they can sign in again.`); }
                        else { setErr(res.data?.message || res.data?.error || "Failed to reactivate user"); }
                      }} style={{ background: "none", border: "none", color: T.grn, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>Reactivate</button>
                    </>
                  ) : (
                    <button onClick={async () => {
                      if (!window.confirm(`Deactivate ${u.name}? They will be signed out and unable to log in (any open session is rejected on its next action). This is reversible.`)) return;
                      const res = await apiCallEx(`/users/${u.id}/deactivate`, { method: "POST" });
                      if (res.ok && res.data?.success) { refreshUsers(); setMsg(`${u.name} deactivated — they can no longer sign in.`); }
                      else { setErr(res.data?.message || res.data?.error || "Failed to deactivate user"); }
                    }} style={{ background: "none", border: "none", color: T.orn, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>Deactivate</button>
                  )}
                  <button onClick={async () => {
                    if (!window.confirm(`PERMANENTLY delete ${u.name}? This is irreversible and removes history. Deactivate is preferred. Continue?`)) return;
                    const res = await apiCallEx(`/users/${u.id}?force=confirm`, { method: "DELETE" });
                    if (res.ok && res.data?.success) { setUsers(prev => prev.filter((x: any) => x.id !== u.id)); setMsg("User permanently deleted"); }
                    else if (res.status === 409 && res.data?.error === "delete_blocked") { setErr(res.data.message || "Cannot delete: user is still referenced. Deactivate instead."); }
                    else { setErr(res.data?.message || res.data?.error || "Failed to delete user"); }
                  }} style={{ background: "none", border: "none", color: T.txD, cursor: "pointer", fontSize: 11, fontFamily: "inherit" }} title="Permanent, irreversible. Deactivate is the safe option.">Delete permanently</button>
                </div>
              </div>
            ))
          }
        </Card>

        {/* Permissions */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Permissions Matrix</div>
          <div style={{ fontSize: 11, color: T.txM, lineHeight: 1.7 }}>
            <div style={{ marginBottom: 6 }}><strong style={{ color: T.tx }}>System Admin:</strong> Full access, configure admin, manage all users and settings</div>
            <div style={{ marginBottom: 6 }}><strong style={{ color: T.tx }}>Legal Ops:</strong> Assign matters, edit metadata, validate AI, manage repo, reports, bulk import, drafting</div>
            <div style={{ marginBottom: 6 }}><strong style={{ color: T.tx }}>Commercial Ops:</strong> Submit requests, assign commercial matters, validate AI, manage repo, bulk import, drafting</div>
            <div style={{ marginBottom: 6 }}><strong style={{ color: T.tx }}>Lawyer:</strong> View assigned, edit status, upload drafts, drafting, add reviewers, reports</div>
            <div style={{ marginBottom: 6 }}><strong style={{ color: T.tx }}>Contract Manager:</strong> View assigned, edit status, upload drafts, drafting, add reviewers, own matter reports</div>
            <div style={{ marginBottom: 6 }}><strong style={{ color: T.tx }}>Business Reviewer:</strong> View assigned reviews only, provide feedback, view documents, own matter reports</div>
            <div style={{ marginBottom: 6 }}><strong style={{ color: T.tx }}>Requestor:</strong> Submit requests, upload drafts, view own requests, respond to questions</div>
            <div><strong style={{ color: T.tx }}>Executive:</strong> Submit requests, view dashboards, search, read-only access</div>
          </div>
        </Card>

        {/* AI Config */}
        <Card><div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>AI Config</div>{[["Model", "Multi-provider (tiered routing)"], ["Pipeline", "Upload → OCR → Classify → Extract → Infer → Validate → Store"], ["Confidence", "High ≥90% · Med 70-89% · Low <70%"], ["Status", "Connected"]].map(([l, v]) => <div key={l} style={{ padding: "8px 10px", backgroundColor: T.bg3, borderRadius: 4, marginBottom: 4 }}><div style={{ fontSize: 9, color: T.txD, textTransform: "uppercase", marginBottom: 2 }}>{l}</div><div style={{ fontSize: 11, color: T.tx }}>{v}</div></div>)}</Card>

        {/* Salesforce */}
        <Card><div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Salesforce Integration</div>{[["Instance", SF_INSTANCE], ["API Version", "REST API v59.0"], ["Auth", "OAuth 2.0 (Connected App)"], ["Sync", "Bidirectional, real-time"], ["Objects", "Opportunity, Account, Contact"], ["Custom Fields", "Territory, Product Line, Deal Type"], ["Status", "Connected"]].map(([l, v]) => <div key={l} style={{ padding: "8px 10px", backgroundColor: T.bg3, borderRadius: 4, marginBottom: 4 }}><div style={{ fontSize: 9, color: T.txD, textTransform: "uppercase", marginBottom: 2 }}>{l}</div><div style={{ fontSize: 11, color: T.tx }}>{v}</div></div>)}</Card>

        {/* IP Allow List */}
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>IP Allow List</div>
            <Badge c={ipEnabled ? T.grn : T.txD} b={ipEnabled ? T.grnS : T.bg4}>{ipEnabled ? "Enforcing" : "Disabled"}</Badge>
          </div>
          <div style={{ fontSize: 11, color: T.txM, marginBottom: 12, padding: "8px 10px", backgroundColor: T.bg3, borderRadius: 4 }}>
            {ipEnabled ? "IP allow-listing is active. Only listed IPs can access the API." : "IP allow-listing is disabled. Set IP_ALLOWLIST_ENABLED=true in server configuration to enforce."}
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            <input value={newIp.ip_address} onChange={e => setNewIp((p: any) => ({ ...p, ip_address: e.target.value }))} placeholder="IP address" style={{ flex: 1, padding: "6px 10px", borderRadius: 5, border: `1px solid ${T.brd}`, fontSize: 11, backgroundColor: T.bg3, color: T.tx, outline: "none", fontFamily: "monospace" }} />
            <input value={newIp.description} onChange={e => setNewIp((p: any) => ({ ...p, description: e.target.value }))} placeholder="Description" style={{ flex: 1, padding: "6px 10px", borderRadius: 5, border: `1px solid ${T.brd}`, fontSize: 11, backgroundColor: T.bg3, color: T.tx, outline: "none" }} />
            <Btn v="primary" sz="sm" onClick={() => { addIp(); }} disabled={!newIp.ip_address}>Add</Btn>
          </div>
          {ipEntries.length === 0 && <div style={{ fontSize: 11, color: T.txD, textAlign: "center", padding: 8 }}>No IPs configured</div>}
          {ipEntries.map(e => (
            <div key={e.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
              <div>
                <span style={{ fontSize: 12, fontFamily: "monospace", color: T.tx, fontWeight: 600 }}>{e.ip_address}</span>
                {e.description && <span style={{ fontSize: 11, color: T.txD, marginLeft: 8 }}>{e.description}</span>}
              </div>
              <button onClick={() => deleteIp(e.id)} style={{ background: "none", border: "none", color: T.red, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Remove</button>
            </div>
          ))}
        </Card>
      </div>

      {/* Danger Zone */}
      <Card style={{ borderLeft: `3px solid ${T.red}`, marginTop: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.red, marginBottom: 8 }}>Danger Zone</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 12, color: T.tx, fontWeight: 500 }}>Purge Repository</div>
            <div style={{ fontSize: 11, color: T.txD }}>Permanently delete all contracts, requests, extractions, and uploaded files. Users and configuration are preserved.</div>
          </div>
          <Btn v="danger" sz="sm" onClick={() => setShowPurge(true)}>Purge All Data</Btn>
        </div>
        {purgeResult && (
          <div style={{ marginTop: 10, padding: 10, backgroundColor: T.redS, borderRadius: 4, fontSize: 11, color: T.red }}>
            Repository purged. Deleted: {purgeResult.contracts} contracts, {purgeResult.requests} requests, {purgeResult.ai_fields} AI fields, {purgeResult.versions} versions.
          </div>
        )}
      </Card>

      {/* Edit User Modal */}
      {editUser && (
        <Modal open={!!editUser} onClose={() => setEditUser(null)} title="Edit User Profile">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Inp label="Name" value={editForm.name || ""} onChange={v => setEditForm((p: any) => ({ ...p, name: v }))} />
            <Inp label="Email" value={editForm.email || ""} onChange={v => setEditForm((p: any) => ({ ...p, email: v }))} />
            <Sel label="Role" value={editForm.role || ""} onChange={v => setEditForm((p: any) => ({ ...p, role: v }))} options={Object.entries(roleLabels).map(([value, label]) => ({ value, label }))} />
            <Inp label="Specialty" value={editForm.specialty || ""} onChange={v => setEditForm((p: any) => ({ ...p, specialty: v }))} />
            <Inp label="Department" value={editForm.department || ""} onChange={v => setEditForm((p: any) => ({ ...p, department: v }))} style={{ gridColumn: "1 / -1" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Btn v="secondary" onClick={() => setEditUser(null)}>Cancel</Btn>
            <Btn v="primary" onClick={async () => {
              setSaving(true);
              const res = await apiCall(`/users/${editUser.id}`, { method: "PUT", body: JSON.stringify(editForm) });
              if (res?.user) { setUsers(prev => prev.map((u: any) => u.id === editUser.id ? { ...u, ...res.user } : u)); setEditUser(null); setMsg("User updated successfully"); }
              else { setErr("Failed to update user"); }
              setSaving(false);
            }} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</Btn>
          </div>
        </Modal>
      )}

      {/* Send Welcome Modal */}
      {sendWelcomeUser && (
        <Modal open={!!sendWelcomeUser} onClose={() => setSendWelcomeUser(null)} title={`Send welcome email: ${sendWelcomeUser.name}`} w={460}>
          <div style={{ padding: "12px 0" }}>
            <div style={{ fontSize: 12, color: T.txM, marginBottom: 12 }}>
              Sending a welcome email to <strong>{sendWelcomeUser.email}</strong> will:
            </div>
            <ul style={{ fontSize: 12, color: T.tx, lineHeight: 1.7, paddingLeft: 18, marginTop: 0, marginBottom: 14 }}>
              <li>Generate a fresh 48-hour password-set link.</li>
              <li>Invalidate any prior unused invite tokens for this user.</li>
              <li>Set the &quot;must change password&quot; flag, so even if they remember an old password, they will be forced to rotate on next login.</li>
              <li>Send the welcome email via Microsoft Graph.</li>
            </ul>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.tx, marginBottom: 16, cursor: "pointer" }}>
              <input type="checkbox" checked={sendWelcomeRequire2FA} onChange={e => setSendWelcomeRequire2FA(e.target.checked)} />
              Also require two-factor authentication for this user (sets totp_required = true)
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Btn v="secondary" sz="sm" onClick={() => setSendWelcomeUser(null)} disabled={sendWelcomeSubmitting}>Cancel</Btn>
              <Btn
                v="primary"
                sz="sm"
                disabled={sendWelcomeSubmitting}
                onClick={async () => {
                  setSendWelcomeSubmitting(true);
                  try {
                    const r = await apiCall(`/users/${sendWelcomeUser.id}/send-welcome`, {
                      method: "POST",
                      body: JSON.stringify({ require_2fa: sendWelcomeRequire2FA }),
                    });
                    if (r?.ok) {
                      setMsg(
                        r.email_sent
                          ? `Welcome email sent to ${sendWelcomeUser.email}.`
                          : `Invite token created for ${sendWelcomeUser.email} but email delivery failed (${r.email_error || "unknown error"}). You can retry by clicking Send Welcome again.`
                      );
                      refreshUsers();
                      setSendWelcomeUser(null);
                    } else {
                      setErr(r?.error || "Failed to send welcome email");
                    }
                  } catch (e) {
                    setErr("Connection error");
                  } finally {
                    setSendWelcomeSubmitting(false);
                  }
                }}
              >
                {sendWelcomeSubmitting ? "Sending..." : "Send Welcome Email"}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Reset Password Modal */}
      {resetPwUser && (
        <Modal open={!!resetPwUser} onClose={() => setResetPwUser(null)} title={"Reset Password: " + resetPwUser.name} w={420}>
          <div style={{ padding: "12px 0" }}>
            <div style={{ fontSize: 12, color: T.txM, marginBottom: 12 }}>Set a new password for <strong>{resetPwUser.email}</strong></div>
            <Inp label="New Password" value={resetPwValue} onChange={v => setResetPwValue(v)} placeholder="Min 8 characters" />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <Btn v="secondary" sz="sm" onClick={() => setResetPwUser(null)}>Cancel</Btn>
              <Btn v="primary" sz="sm" disabled={resetPwValue.length < 8} onClick={async () => {
                const r = await apiCall("/users/" + resetPwUser.id + "/reset-password", { method: "PUT", body: JSON.stringify({ newPassword: resetPwValue }) });
                if (r?.success) { setMsg(r.message); setResetPwUser(null); setResetPwValue(""); }
                else { setErr(r?.error || "Failed to reset password"); }
              }}>Reset Password</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Purge Confirmation Modal */}
      {showPurge && (
        <Modal open={showPurge} onClose={() => { setShowPurge(false); setPurgeConfirm(""); }} title="Confirm Repository Purge">
          <div style={{ padding: "12px 0" }}>
            <div style={{ fontSize: 12, color: T.red, fontWeight: 600, marginBottom: 8 }}>This action cannot be undone.</div>
            <div style={{ fontSize: 12, color: T.tx, marginBottom: 12 }}>This will permanently delete all contracts, requests, AI extractions, document versions, notifications, and uploaded files. User accounts and system configuration will be preserved.</div>
            <div style={{ fontSize: 12, color: T.tx, marginBottom: 8 }}>Type <strong>PURGE</strong> to confirm:</div>
            <input value={purgeConfirm} onChange={e => setPurgeConfirm(e.target.value.trim())} placeholder="Type PURGE to confirm" style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 4, border: `1px solid ${purgeConfirm.trim().toUpperCase() === "PURGE" ? T.red : T.brd}`, backgroundColor: T.bg3, color: T.tx, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            <Btn v="secondary" onClick={() => { setShowPurge(false); setPurgeConfirm(""); }}>Cancel</Btn>
            <Btn v="danger" onClick={async () => {
              if (purgeConfirm.trim().toUpperCase() !== "PURGE") return;
              setPurging(true);
              try {
                const token = localStorage.getItem("kontractai_token");
                const resp = await fetch("/api/contracts/purge", {
                  method: "POST",
                  headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
                  body: JSON.stringify({ confirmation: "PURGE" })
                });
                const data = await resp.json();
                if (data?.success) {
                  alert("Repository purged successfully. Deleted: " + data.deleted.contracts + " contracts, " + data.deleted.requests + " requests, " + data.deleted.ai_fields + " AI fields.");
                  window.location.reload();
                } else { setErr(data.error || "Purge failed"); }
              } catch (e: any) { setErr("Purge failed: " + e.message); }
              setPurging(false);
            }} disabled={purgeConfirm.trim().toUpperCase() !== "PURGE" || purging}>{purging ? "Purging..." : "Permanently Purge All Data"}</Btn>
          </div>
        </Modal>
      )}
      </>}
    </div>
  );
};
